/**
 * Cours particuliers — réservation élève (son tuteur, dans l'app) et invité
 * (fiche publique marketing, route publique `/reserver/$slotId`). Les 2 RPC
 * consommées ici (`list_tutor_bookable_slots`, `get_tutor_bookable_slot`) sont
 * `security definer` et postérieures aux types générés → client générique
 * (calque `apps/app/src/lib/live-campaigns.ts`).
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { RendezVousKind } from '@jeraisonne/db';
import { isRendezVousUpcoming, toEndedAtMs } from '@jeraisonne/db/visio';
import { supabase } from '@/lib/supabase';
import { fetchArchivedReservationIds } from '@/lib/rendezvous-archive';

const db = supabase as unknown as SupabaseClient;

export interface BookableSlot {
  id: string;
  starts_at: string;
  ends_at: string;
  level: string;
  chapter_title: string | null;
  price_cents: number;
  capacity: number;
  seats_left: number;
  kind: RendezVousKind;
}

export interface BookableSlotDetail extends BookableSlot {
  tutor_id: string;
  tutor_name: string;
  bookable: boolean;
}

/** Créneaux ouverts du tuteur (élève déjà lié — écran "Réserver un cours" de l'app). */
export function useTutorBookableSlots(tutorId: string | undefined) {
  return useQuery({
    queryKey: ['tutor-bookable-slots', tutorId],
    enabled: !!tutorId,
    queryFn: async (): Promise<BookableSlot[]> => {
      const { data, error } = await db.rpc('list_tutor_bookable_slots', {
        p_tutor_id: tutorId,
        p_limit: 20,
      });
      if (error) throw new Error(error.message);
      return (data ?? []) as BookableSlot[];
    },
  });
}

export interface MyUpcomingBooking {
  reservationId: string;
  tutorName: string;
  startsAt: string;
  endsAt: string;
  kind: RendezVousKind;
}

type BookingReservationRow = {
  id: string;
  status: string;
  /** Fin RÉELLE (le tuteur a terminé la réunion, migration 0249) — le rendez-vous est alors passé immédiatement. */
  ended_at: string | null;
  tutor_booking_slots: {
    starts_at: string;
    ends_at: string;
    kind: RendezVousKind;
    profiles: { display_name: string } | null;
  } | null;
};

const BOOKING_RESERVATION_SELECT =
  'id, status, ended_at, tutor_booking_slots(starts_at, ends_at, kind, profiles(display_name:visible_name))';

/**
 * Réservations confirmées à venir de l'élève connecté — alimente la section
 * "Mes prochains cours" (bouton "Rejoindre la salle"). `tutor_booking_reservations`
 * a déjà une policy `own_select` (student_id = auth.uid()) : aucune RLS
 * nouvelle nécessaire. Pas de disambiguïsation de FK requise : contrairement à
 * `video_rooms`/`tutor_links`, `tutor_booking_slots` n'a qu'une seule FK vers
 * `profiles` (tutor_id). Exclut les rendez-vous archivés par l'élève lui-même
 * (croix sur la carte, migration 0240) — retrouvables dans "Rendez-vous passés"
 * (`useMyPastBookings`).
 */
export function useMyUpcomingBookings(studentId: string | undefined) {
  return useQuery({
    queryKey: ['my-upcoming-bookings', studentId],
    enabled: !!studentId,
    queryFn: async (): Promise<MyUpcomingBooking[]> => {
      const [{ data, error }, archivedIds] = await Promise.all([
        db
          .from('tutor_booking_reservations')
          .select(BOOKING_RESERVATION_SELECT)
          .eq('student_id', studentId!)
          .eq('status', 'confirmed'),
        fetchArchivedReservationIds(studentId!),
      ]);
      if (error) throw new Error(error.message);

      const now = Date.now();
      return ((data ?? []) as unknown as BookingReservationRow[])
        .filter(
          (row) =>
            row.tutor_booking_slots &&
            !archivedIds.has(row.id) &&
            isRendezVousUpcoming(
              now,
              new Date(row.tutor_booking_slots.ends_at).getTime(),
              toEndedAtMs(row.ended_at),
            ),
        )
        .map((row) => ({
          reservationId: row.id,
          tutorName: row.tutor_booking_slots!.profiles?.display_name ?? 'Ton prof',
          startsAt: row.tutor_booking_slots!.starts_at,
          endsAt: row.tutor_booking_slots!.ends_at,
          kind: row.tutor_booking_slots!.kind,
        }))
        .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    },
  });
}

export interface MyPastBooking extends MyUpcomingBooking {
  status: string;
  archivedByMe: boolean;
}

/**
 * "Rendez-vous passés" côté élève : réservations terminées (ou plus joignables)
 * OU archivées manuellement par l'élève — l'endroit où retrouver un rendez-vous
 * masqué du dashboard (notes/résumé d'entretien via `/rendez-vous/$reservationId`).
 */
export function useMyPastBookings(studentId: string | undefined) {
  return useQuery({
    queryKey: ['my-past-bookings', studentId],
    enabled: !!studentId,
    queryFn: async (): Promise<MyPastBooking[]> => {
      const [{ data, error }, archivedIds] = await Promise.all([
        db
          .from('tutor_booking_reservations')
          .select(BOOKING_RESERVATION_SELECT)
          .eq('student_id', studentId!)
          .order('created_at', { ascending: false })
          .limit(100),
        fetchArchivedReservationIds(studentId!),
      ]);
      if (error) throw new Error(error.message);

      const now = Date.now();
      return ((data ?? []) as unknown as BookingReservationRow[])
        .filter(
          (
            row,
          ): row is BookingReservationRow & {
            tutor_booking_slots: NonNullable<BookingReservationRow['tutor_booking_slots']>;
          } => Boolean(row.tutor_booking_slots),
        )
        .filter((row) => {
          const archived = archivedIds.has(row.id);
          const stillUpcoming =
            row.status === 'confirmed' &&
            isRendezVousUpcoming(
              now,
              new Date(row.tutor_booking_slots.ends_at).getTime(),
              toEndedAtMs(row.ended_at),
            );
          return archived || !stillUpcoming;
        })
        .map((row) => ({
          reservationId: row.id,
          tutorName: row.tutor_booking_slots.profiles?.display_name ?? 'Ton prof',
          startsAt: row.tutor_booking_slots.starts_at,
          endsAt: row.tutor_booking_slots.ends_at,
          kind: row.tutor_booking_slots.kind,
          status: row.status,
          archivedByMe: archivedIds.has(row.id),
        }))
        .sort((a, b) => b.startsAt.localeCompare(a.startsAt));
    },
  });
}

/** Détail public d'UN créneau par son id (route publique /reserver/$slotId). */
export function useBookableSlot(slotId: string | undefined) {
  return useQuery({
    queryKey: ['bookable-slot', slotId],
    enabled: !!slotId,
    queryFn: async (): Promise<BookableSlotDetail | null> => {
      const { data, error } = await db
        .rpc('get_tutor_bookable_slot', { p_slot_id: slotId })
        .maybeSingle();
      if (error) throw new Error(error.message);
      return (data as BookableSlotDetail | null) ?? null;
    },
  });
}

export interface BookSlotInput {
  slotId: string;
  returnTo: string;
  guestEmail?: string;
  guestName?: string;
}

/**
 * Réserve + paye un créneau. Fonctionne pour un élève connecté (JWT attaché
 * automatiquement par `functions.invoke`, guestEmail/guestName ignorés côté
 * serveur) ET pour un invité (aucune session → guestEmail requis).
 */
export function useBookSlot() {
  return useMutation({
    mutationFn: async (input: BookSlotInput): Promise<{ url: string; reservation_id: string }> => {
      const { data, error } = await supabase.functions.invoke<{
        url: string;
        reservation_id: string;
      }>('tutor-booking-checkout', {
        body: {
          slot_id: input.slotId,
          return_to: input.returnTo,
          ...(input.guestEmail ? { guest_email: input.guestEmail } : {}),
          ...(input.guestName ? { guest_name: input.guestName } : {}),
        },
      });
      if (error) throw new Error(error.message);
      if (!data?.url) throw new Error('checkout_url_missing');
      return data;
    },
  });
}
